import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, MapPin, PackageCheck, Printer, RefreshCw, ShieldCheck, Truck } from "lucide-react";
import { api } from "../../services/api";
import { formatUsDateTime } from "../../utils/timezone";
import { StatusPill } from "./AdminPanelParts";
import { ToastStack, useToast } from "./useToast";

const DEFAULT_PARCEL = { length: 10, width: 8, height: 4, weight: "" };
const pickupCarriers = ["USPS", "UPS", "FedEx", "DHL"];

function money(value, currency = "USD") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD" }).format(Number(value || 0));
}

function orderDestination(order) {
  const address = order?.shippingAddress || {};
  return [address.line1, address.city, address.state, address.postalCode, address.country].filter(Boolean).join(", ") || "Address missing";
}

function orderItemCount(order) {
  return (order?.items || []).reduce((sum, item) => sum + Number(item.quantity || 1), 0);
}

function orderWeight(order) {
  return Math.max(0.1, (order?.items || []).reduce((sum, item) => sum + Number(item.weightLb || 1) * Number(item.quantity || 1), 0) || 1);
}

function Step({ active, complete, number, title, text }) {
  return (
    <div className={`rounded-2xl border p-4 ${active ? "border-blue-200 bg-blue-50" : "border-gray-100 bg-white"}`}>
      <div className="flex items-start gap-3">
        <span className={`flex h-8 w-8 flex-none items-center justify-center rounded-full text-xs font-black ${complete ? "bg-emerald-500 text-white" : active ? "bg-[#4391d1] text-white" : "bg-gray-100 text-gray-400"}`}>
          {complete ? <CheckCircle2 size={16} /> : number}
        </span>
        <div><p className="text-sm font-black text-gray-900">{title}</p><p className="mt-1 text-xs font-semibold leading-5 text-gray-500">{text}</p></div>
      </div>
    </div>
  );
}

export function ShippingWorkspace({ embedded = false }) {
  const [searchParams] = useSearchParams();
  const requestedOrderId = searchParams.get("order") || "";
  const [shippoStatus, setShippoStatus] = useState(null);
  const [shippoOrders, setShippoOrders] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [parcel, setParcel] = useState(DEFAULT_PARCEL);
  const [rates, setRates] = useState([]);
  const [rateMessages, setRateMessages] = useState([]);
  const [selectedRateId, setSelectedRateId] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [createdLabel, setCreatedLabel] = useState(null);
  const [pickupCarrier, setPickupCarrier] = useState("USPS");
  const [pickupRequests, setPickupRequests] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [pickupLoading, setPickupLoading] = useState(false);
  const { dismiss, toast, toasts } = useToast();

  const selectedOrder = useMemo(() => shippoOrders.find((order) => order._id === selectedOrderId), [selectedOrderId, shippoOrders]);
  const selectedRate = useMemo(() => rates.find((rate) => rate.objectId === selectedRateId), [rates, selectedRateId]);
  const cheapestRateId = rates[0]?.objectId;
  const fastestRateId = useMemo(() => rates.filter((rate) => Number(rate.estimatedDays) > 0).sort((a, b) => Number(a.estimatedDays) - Number(b.estimatedDays))[0]?.objectId, [rates]);

  const loadShippo = useCallback(async () => {
    setPageLoading(true);
    try {
      const [statusData, orderData, pickupData] = await Promise.all([
        api.get("/shippo/status"),
        api.get("/shippo/orders/ready"),
        api.get("/shippo/pickups"),
      ]);
      const nextOrders = orderData.orders || [];
      setShippoStatus(statusData);
      setShippoOrders(nextOrders);
      setPickupRequests(pickupData.pickupRequests || []);
      setSelectedOrderId((current) => {
        if (nextOrders.some((order) => order._id === current)) return current;
        if (nextOrders.some((order) => order._id === requestedOrderId)) return requestedOrderId;
        return nextOrders[0]?._id || "";
      });
    } catch (err) {
      setShippoStatus({ enabled: false, error: err.message });
      setShippoOrders([]);
      setPickupRequests([]);
      toast(err.message, "error");
    } finally {
      setPageLoading(false);
    }
  }, [requestedOrderId, toast]);

  useEffect(() => { loadShippo(); }, [loadShippo]);

  const selectOrder = (orderId) => {
    setSelectedOrderId(orderId);
    setParcel(DEFAULT_PARCEL);
    setRates([]);
    setRateMessages([]);
    setSelectedRateId("");
    setConfirming(false);
  };

  const updateParcel = (event) => {
    setParcel((current) => ({ ...current, [event.target.name]: event.target.value }));
    setRates([]);
    setSelectedRateId("");
    setConfirming(false);
  };

  const fetchRates = async () => {
    if (!selectedOrderId) return toast("Select a paid order first", "warning");
    if ([parcel.length, parcel.width, parcel.height].map(Number).some((value) => !Number.isFinite(value) || value <= 0)) {
      return toast("Enter valid parcel dimensions before comparing rates", "warning");
    }
    setRatesLoading(true);
    setRates([]);
    setRateMessages([]);
    setSelectedRateId("");
    setConfirming(false);
    try {
      const data = await api.post(`/shippo/orders/${selectedOrderId}/rates`, {
        parcel: {
          length: Number(parcel.length), width: Number(parcel.width), height: Number(parcel.height),
          weight: Number(parcel.weight || orderWeight(selectedOrder)),
        },
      });
      const nextRates = data.rates || [];
      setRates(nextRates);
      setRateMessages(data.messages || []);
      setSelectedRateId(nextRates[0]?.objectId || "");
      toast(nextRates.length ? `${nextRates.length} Shippo rates ready` : "Shippo returned no rates", nextRates.length ? "success" : "warning");
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setRatesLoading(false);
    }
  };

  const buyLabel = async () => {
    if (!selectedOrderId || !selectedRate?.objectId) return;
    setPurchasing(true);
    try {
      const data = await api.post(`/shippo/orders/${selectedOrderId}/label`, { rateId: selectedRate.objectId, labelFileType: "PDF_4x6" });
      setCreatedLabel({ ...data.order, rate: selectedRate });
      setRates([]);
      setSelectedRateId("");
      setConfirming(false);
      toast(`Label created for ${data.order.orderNumber}`, "success");
      await loadShippo();
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setPurchasing(false);
    }
  };

  const requestPickup = async () => {
    setPickupLoading(true);
    try {
      const data = await api.post("/shippo/pickups", { carrier: pickupCarrier.toLowerCase(), pickupDate: new Date().toISOString() });
      const nextPickup = { ...data.pickupRequest, requestedAt: data.pickupRequest?.requestedAt || new Date().toISOString() };
      setPickupRequests((current) => [nextPickup, ...current].slice(0, 6));
      toast(data.warning || `${pickupCarrier} pickup requested`, data.warning ? "warning" : "success");
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setPickupLoading(false);
    }
  };

  const content = (
    <>
      <ToastStack dismiss={dismiss} toasts={toasts} />
      <div className={`${embedded ? "mb-4" : "mb-8"} flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between`}>
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-widest text-[#4391d1]">Fulfillment workspace</p>
          <h1 className={embedded ? "text-2xl font-black text-gray-900" : "text-3xl font-black text-gray-900"}>Shipping Center</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-500">Review a paid order, compare live Shippo rates, confirm postage, and print the label from one guided workflow.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-black text-gray-700 hover:border-[#4391d1] hover:text-[#4391d1]" to="/dashboard/orders">View all orders</Link>
          <button className="inline-flex items-center gap-2 rounded-xl bg-gray-950 px-4 py-3 text-sm font-black text-white disabled:opacity-50" disabled={pageLoading} onClick={loadShippo} type="button"><RefreshCw className={`h-4 w-4 ${pageLoading ? "animate-spin" : ""}`} /> Refresh queue</button>
        </div>
      </div>

      {shippoStatus?.error && <div className="mb-5 rounded-2xl border border-red-100 bg-red-50 p-4 text-sm font-bold text-red-700">{shippoStatus.error}</div>}

      {shippoStatus?.fromAddress && (
        <div className="mb-5 rounded-2xl border border-blue-100 bg-blue-50 p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-3">
              <span className="rounded-xl bg-white p-2 text-[#4391d1]"><MapPin size={18} /></span>
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-blue-700">Ship from</p>
                <p className="mt-1 font-black text-blue-950">{shippoStatus.fromAddress.name || shippoStatus.fromAddress.company}</p>
                <p className="mt-1 text-sm font-semibold leading-6 text-blue-800">
                  {[shippoStatus.fromAddress.street1, shippoStatus.fromAddress.street2, [shippoStatus.fromAddress.city, shippoStatus.fromAddress.state, shippoStatus.fromAddress.zip].filter(Boolean).join(", ")].filter(Boolean).map((line, index) => <span className="block" key={`${line}-${index}`}>{line}</span>)}
                  {shippoStatus.fromAddress.phone && <span className="mt-1 block">{shippoStatus.fromAddress.phone}</span>}
                </p>
              </div>
            </div>
            <StatusPill tone={shippoStatus.fromAddressConfigured ? "good" : "warn"}>
              {shippoStatus.fromAddressConfigured ? "Verified sender" : "Sender setup needed"}
            </StatusPill>
          </div>
          {!shippoStatus.fromAddressConfigured && (
            <p className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs font-bold leading-5 text-amber-800">
              Complete the Shippo sender configuration before requesting rates. Missing: {(shippoStatus.missingFromAddressFields || []).join(", ")}.
            </p>
          )}
        </div>
      )}

      {createdLabel && (
        <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3"><span className="rounded-full bg-emerald-500 p-2 text-white"><CheckCircle2 size={20} /></span><div><p className="font-black text-emerald-950">Label ready for {createdLabel.orderNumber}</p><p className="mt-1 text-sm font-semibold text-emerald-800">{[createdLabel.carrier, createdLabel.trackingNumber].filter(Boolean).join(" ")} · Order marked shipped and customer notified.</p></div></div>
            <div className="flex flex-wrap gap-2"><Link className="rounded-lg border border-emerald-200 bg-white px-3 py-2 text-xs font-black text-emerald-800" to={`/dashboard/orders/${createdLabel._id}`}>View order</Link><a className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-black text-white" href={createdLabel.shippingLabelUrl} rel="noreferrer" target="_blank"><Printer size={14} /> Open & print label</a></div>
          </div>
        </div>
      )}

      <div className="mb-5 grid gap-3 md:grid-cols-3">
        <Step active={!selectedOrder} complete={Boolean(selectedOrder)} number="1" title="Select order" text="Choose a paid order waiting for a label." />
        <Step active={Boolean(selectedOrder) && !rates.length} complete={Boolean(rates.length)} number="2" title="Compare rates" text="Confirm parcel details and load Shippo options." />
        <Step active={Boolean(rates.length)} complete={Boolean(createdLabel)} number="3" title="Buy & print" text="Review the selected cost before purchasing postage." />
      </div>

      <section className="grid gap-5 xl:grid-cols-[minmax(0,1.6fr)_minmax(320px,0.8fr)]">
        <div className="space-y-5">
          <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3"><div><h2 className="text-lg font-black text-gray-900">1. Order and parcel</h2><p className="mt-1 text-xs font-semibold text-gray-500">Only paid processing orders without tracking appear here.</p></div><StatusPill tone={shippoStatus?.enabled ? "good" : "warn"}>{shippoStatus?.enabled ? `Shippo ${shippoStatus.mode || "connected"}` : "Shippo unavailable"}</StatusPill></div>
            <label className="mt-4 block"><span className="mb-2 block text-xs font-black uppercase tracking-wide text-gray-500">Ready order ({shippoOrders.length})</span><select className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-bold outline-none focus:border-[#4391d1]" disabled={!shippoOrders.length || pageLoading} onChange={(event) => selectOrder(event.target.value)} value={selectedOrderId}><option value="">Select an order</option>{shippoOrders.map((order) => <option key={order._id} value={order._id}>{order.orderNumber} · {order.shippingAddress?.fullName || order.user?.name} · {orderDestination(order)}</option>)}</select></label>
            {!shippoOrders.length && shippoStatus?.enabled && !pageLoading && <div className="mt-4 rounded-xl bg-emerald-50 p-4 text-sm font-bold text-emerald-800">All paid orders currently have labels. Nothing is waiting.</div>}

            {selectedOrder && (
              <>
                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  <div className="rounded-xl bg-gray-50 p-3"><p className="text-xs font-bold text-gray-400">Customer</p><p className="mt-1 text-sm font-black text-gray-900">{selectedOrder.shippingAddress?.fullName || selectedOrder.user?.name}</p></div>
                  <div className="rounded-xl bg-gray-50 p-3"><p className="text-xs font-bold text-gray-400">Items</p><p className="mt-1 text-sm font-black text-gray-900">{orderItemCount(selectedOrder)} item{orderItemCount(selectedOrder) === 1 ? "" : "s"}</p></div>
                  <div className="rounded-xl bg-gray-50 p-3"><p className="text-xs font-bold text-gray-400">Customer shipping</p><p className="mt-1 text-sm font-black text-gray-900">{money(selectedOrder.shipping)}</p></div>
                </div>
                <div className="mt-3 flex items-start gap-3 rounded-xl border border-gray-100 p-4"><MapPin className="mt-0.5 flex-none text-[#4391d1]" size={18} /><div><p className="text-xs font-black uppercase text-gray-400">Ship to</p><p className="mt-1 text-sm font-bold leading-6 text-gray-700">{orderDestination(selectedOrder)}</p></div></div>
                <div className="mt-5">
                  <div className="mb-3 flex items-end justify-between gap-3"><div><p className="text-sm font-black text-gray-900">Parcel details</p><p className="mt-1 text-xs font-semibold text-gray-500">Measure the packed box for accurate rates.</p></div><span className="text-xs font-black text-gray-400">inches / pounds</span></div>
                  <div className="grid gap-3 sm:grid-cols-4">
                    {[["length", "Length"], ["width", "Width"], ["height", "Height"], ["weight", "Weight"]].map(([name, label]) => <label key={name}><span className="mb-2 block text-xs font-black uppercase text-gray-500">{label}</span><input className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm font-bold outline-none focus:border-[#4391d1]" min="0.1" name={name} onChange={updateParcel} placeholder={name === "weight" ? orderWeight(selectedOrder).toFixed(1) : ""} step="0.1" type="number" value={parcel[name]} /></label>)}
                  </div>
                  <button className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gray-950 px-4 py-3 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-50" disabled={ratesLoading || !shippoStatus?.enabled || !shippoStatus?.fromAddressConfigured} onClick={fetchRates} type="button"><RefreshCw className={ratesLoading ? "animate-spin" : ""} size={16} /> {ratesLoading ? "Loading Shippo rates..." : "Compare Shippo rates"}</button>
                </div>
              </>
            )}
          </div>

          {rates.length > 0 && (
            <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
              <div><h2 className="text-lg font-black text-gray-900">2. Select a rate</h2><p className="mt-1 text-xs font-semibold text-gray-500">Choose the service that best balances cost and delivery time.</p></div>
              <div className="mt-4 grid gap-3">
                {rates.map((rate) => (
                  <label className={`cursor-pointer rounded-xl border-2 p-4 transition ${selectedRateId === rate.objectId ? "border-[#4391d1] bg-blue-50" : "border-gray-100 hover:border-blue-200"}`} key={rate.objectId}>
                    <div className="flex items-center justify-between gap-4"><div className="flex items-start gap-3"><input checked={selectedRateId === rate.objectId} className="mt-1" name="shippo-rate" onChange={() => { setSelectedRateId(rate.objectId); setConfirming(false); }} type="radio" /><div><div className="flex flex-wrap items-center gap-2"><p className="font-black text-gray-900">{rate.carrier} · {rate.service}</p>{rate.objectId === cheapestRateId && <StatusPill tone="good">Best price</StatusPill>}{rate.objectId === fastestRateId && fastestRateId !== cheapestRateId && <StatusPill tone="info">Fastest</StatusPill>}</div><p className="mt-1 text-xs font-semibold text-gray-500">{rate.durationTerms || (rate.estimatedDays ? `Estimated ${rate.estimatedDays} business days` : "Delivery estimate unavailable")}</p></div></div><span className="whitespace-nowrap text-lg font-black text-[#4391d1]">{money(rate.amount, rate.currency)}</span></div>
                  </label>
                ))}
              </div>
              {rateMessages.length > 0 && <div className="mt-3 rounded-xl bg-amber-50 p-3 text-xs font-bold text-amber-800">{rateMessages.map((message) => message.text || String(message)).join(" ")}</div>}
              {!confirming ? <button className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#4391d1] px-4 py-3 text-sm font-black text-white disabled:opacity-50" disabled={!selectedRate} onClick={() => setConfirming(true)} type="button"><ShieldCheck size={16} /> Review label purchase</button> : (
                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4"><p className="text-sm font-black text-amber-950">Confirm postage purchase</p><p className="mt-1 text-xs font-semibold leading-5 text-amber-800">Shippo will charge {money(selectedRate.amount, selectedRate.currency)} for {selectedRate.carrier} {selectedRate.service}. The order will be marked shipped and tracking will be emailed to the customer.</p><div className="mt-3 flex flex-wrap gap-2"><button className="rounded-lg bg-gray-950 px-4 py-2.5 text-xs font-black text-white disabled:opacity-50" disabled={purchasing} onClick={buyLabel} type="button">{purchasing ? "Purchasing..." : "Confirm & buy label"}</button><button className="rounded-lg border border-amber-200 bg-white px-4 py-2.5 text-xs font-black text-amber-900" disabled={purchasing} onClick={() => setConfirming(false)} type="button">Cancel</button></div></div>
              )}
            </div>
          )}
        </div>

        <aside className="space-y-5">
          <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="flex items-start gap-3"><span className="rounded-xl bg-blue-50 p-2 text-[#4391d1]"><PackageCheck size={18} /></span><div><h2 className="text-lg font-black text-gray-900">Label queue</h2><p className="mt-1 text-xs font-semibold text-gray-500">{shippoOrders.length} paid order{shippoOrders.length === 1 ? "" : "s"} waiting</p></div></div>
            <div className="mt-4 space-y-2">{shippoOrders.slice(0, 5).map((order) => <button className={`w-full rounded-xl border p-3 text-left ${selectedOrderId === order._id ? "border-blue-200 bg-blue-50" : "border-gray-100 hover:bg-gray-50"}`} key={order._id} onClick={() => selectOrder(order._id)} type="button"><div className="flex items-center justify-between gap-2"><span className="text-sm font-black text-gray-900">{order.orderNumber}</span><span className="text-xs font-black text-gray-400">{orderItemCount(order)} items</span></div><p className="mt-1 truncate text-xs font-semibold text-gray-500">{order.shippingAddress?.fullName || order.user?.name} · {orderDestination(order)}</p></button>)}{!shippoOrders.length && <p className="rounded-xl bg-gray-50 p-4 text-sm font-bold text-gray-500">No labels are waiting.</p>}</div>
          </div>
          <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="flex items-start gap-3"><span className="rounded-xl bg-blue-50 p-2 text-[#4391d1]"><Truck size={18} /></span><div><h2 className="text-lg font-black text-gray-900">Carrier pickup</h2><p className="mt-1 text-xs font-semibold text-gray-500">Request pickup after today’s labels are ready.</p></div></div>
            <select className="mt-4 w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-bold" onChange={(event) => setPickupCarrier(event.target.value)} value={pickupCarrier}>{pickupCarriers.map((carrier) => <option key={carrier}>{carrier}</option>)}</select>
            <button className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-[#4391d1] bg-white px-4 py-3 text-sm font-black text-[#4391d1] disabled:opacity-50" disabled={pickupLoading} onClick={requestPickup} type="button"><Truck size={16} /> {pickupLoading ? "Requesting..." : `Request ${pickupCarrier} pickup`}</button>
            <div className="mt-4 rounded-xl bg-gray-50 p-4">{pickupRequests[0] ? <div className="text-sm"><p className="font-black text-gray-900">{pickupRequests[0].carrier} pickup requested</p><p className="mt-1 text-xs font-semibold text-gray-500">{formatUsDateTime(pickupRequests[0].requestedAt, "Not recorded")} · {Number(pickupRequests[0].orders || 0)} ready orders</p></div> : <p className="text-sm font-bold text-gray-500">No pickup request recorded today.</p>}</div>
          </div>
          <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5 text-sm"><p className="flex items-center gap-2 font-black text-blue-950"><ShieldCheck size={17} /> Manual fulfillment policy</p><p className="mt-2 text-xs font-semibold leading-5 text-blue-800">Checkout collects the store’s fixed shipping charge. Shippo postage is purchased here only after an admin reviews the packed parcel and selected carrier rate.</p></div>
        </aside>
      </section>
    </>
  );

  if (embedded) return content;
  return <div className="p-6 md:p-10">{content}</div>;
}

export default function Shippingcenter() {
  return <ShippingWorkspace />;
}
